import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { User, Briefcase, Boxes, Factory } from 'lucide-react';

// Descrizione delle 4 applicazioni. `key` è usata sia come tab role sia per
// il matching della route corrente.
const APPS = [
  { key: 'employee',   path: '/',            icon: User,      labelKey: 'login.role.employee'   },
  { key: 'hr',         path: '/hr',          icon: Briefcase, labelKey: 'login.role.hr'         },
  { key: 'erp',        path: '/erp',         icon: Boxes,     labelKey: 'login.role.erp'        },
  { key: 'production', path: '/production',  icon: Factory,   labelKey: 'login.role.production' },
];

// Un utente può vedere una app se:
// - è admin (accesso completo)
// - la sua role primaria coincide con la key
// - ha il flag *_access corrispondente
// - `employee` è disponibile a chiunque autenticato (chat, timbrature, richieste, buste paga)
const canAccess = (app, user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  if (app.key === 'employee') return true;
  if (user.role === app.key) return true;
  return !!user[`${app.key}_access`];
};

/**
 * Barra di app switching. Mostrata sopra ogni dashboard.
 * Compare solo se l'utente ha accesso ad almeno 2 app (altrimenti è rumore).
 */
const AppSwitcher = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const accessible = APPS.filter(a => canAccess(a, user));
  if (accessible.length < 2) return null;

  // Determina l'app attiva in base al pathname corrente.
  // '/hr' → hr, '/erp' → erp, '/production' → production, altrimenti employee.
  const currentPath = location.pathname;
  let activeKey = 'employee';
  if (currentPath.startsWith('/hr')) activeKey = 'hr';
  else if (currentPath.startsWith('/erp')) activeKey = 'erp';
  else if (currentPath.startsWith('/production')) activeKey = 'production';

  return (
    <div
      data-testid="app-switcher"
      className="bg-slate-900 text-slate-100 border-b border-slate-800"
    >
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-1.5">
          <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-2 hidden sm:inline">
            App
          </span>
          {accessible.map(app => {
            const Icon = app.icon;
            const isActive = app.key === activeKey;
            return (
              <button
                key={app.key}
                data-testid={`app-switcher-${app.key}`}
                onClick={() => navigate(app.path)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t(app.labelKey)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppSwitcher;
